import type {ApiImageryCheckoutItem} from 'app/modules/Remote/api';

import {CartItemV1} from './types';

export {CartProvider} from './CartProvider';
export {CartContext, useCart} from './useCart';
export type {AddItemResult, CartContextValue, CartItemPatch} from './useCart';

export {CartUndoProvider, useRemoveWithUndo} from './removeWithUndo';

export {cartStorageKey, clearStoredCart, readCart, writeCart} from './storage';

export {
  cartItemDeepLink,
  cartItemPropertyLink,
  cartItemOrderBboxString,
  cartPageUrl,
} from './deepLink';
export type {CartItemLinkFields} from './deepLink';

export {
  CART_VERSION,
  MAX_CART_ITEMS,
  cartItemSubsetFeature,
  emptyCart,
  isPartialAoiItem,
  sceneKey,
} from './types';
export type {CartItemSnapshot, CartItemV1, CartV1, SceneKeyParts} from './types';

export {
  cartGroupByOptions,
  groupCartItems,
  propertyGroupingAvailable,
  resolveCartGroupBy,
} from './grouping';
export type {CartDisplayGroup, CartGroupBy} from './grouping';

export {checkoutPreviewQuery} from './checkoutPreview';
export {
  imageryPrepaidsQuery,
  invalidateCheckoutCredit,
  isCoveredByCredit,
  prepaidRemainingCents,
  useCheckoutPrepaids,
} from './prepaidCredit';
export {creditBalanceOf, planCartPayment} from './charges';
export type {CartCharges, CartPaymentPlan, ChargeGroup} from './charges';
export {normalizeCheckoutFailure} from './checkoutErrors';
export type {CheckoutFailure, CheckoutItemRejection} from './checkoutErrors';

export {
  cartFullMessage,
  cartTotalAcres,
  cartTotalCents,
  formatAcresLabel,
  formatBilledAcresLabel,
  formatItemPrice,
  formatOrderedItemPrice,
  formatPerAcrePrice,
  formatSensingDate,
  itemPriceCents,
  orderedItemPriceCents,
  resolutionLabel,
  sceneCountLabel,
  sceneMetaParts,
} from './format';

export {CartItemRow} from './CartItemRow';
export type {CartItemRowProps} from './CartItemRow';

export {CartItemGroupCard} from './CartItemGroupCard';
export {useCollapsedGroups} from './CartItemGroupCard/useCollapsedGroups';
export type {CartItemGroupCardProps} from './CartItemGroupCard';

export {SnapshotThumbnail} from './SnapshotThumbnail';
export type {SnapshotThumbnailProps} from './SnapshotThumbnail';

export {isCartEnabled, isPayNowEnabled} from './access';

export {checkoutAttemptStore} from './attempts';
export type {CheckoutAttemptStore} from './attempts';

export {
  CHALLENGE_UNAVAILABLE_MESSAGE,
  asksForCard,
  buyerMessage,
  chargedCents,
  checkoutContents,
  checkoutOptions,
  checkoutOutcome,
  dedupeNotices,
  isInFlight,
  outdatesCredit,
  paymentNotice,
  settleCheckout,
  sleep,
  submitAttempt,
  withCardRequest,
} from './payment';
export type {CheckoutAttempt, CheckoutOutcome, PaymentNotice} from './payment';

/**
 * Narrows a stored item to exactly what the checkout endpoint accepts, dropping the client-only
 * `id`, `addedAt`, and `snapshot`.
 */
export function toCheckoutItem(item: CartItemV1): ApiImageryCheckoutItem {
  return {
    projectId: item.projectId,
    featureCollectionId: item.featureCollectionId,
    featureId: item.featureId,
    sourceId: item.sourceId,
    sensingTime: item.sensingTime,
    centsPerAcre: item.centsPerAcre,
    billableAcres: item.billableAcres,
    ...(item.geometry ? {geometry: item.geometry} : {}),
  };
}
